import axios from "axios";
import {
  AppFeature,
  EditionStr,
  InformationType,
  LoggedUser,
  toEdition,
} from "../models";
import { apiCall } from "./ApiResult.model";
import { cord, formatInput, gql2 } from "@/gql/lib";
import {
  AxisType,
  CountParametersTier1Tier2,
  ActionEnum,
  ObjectLabelHistoryItem,
} from "@/gql/types";
import { SharedState } from "../state";
import moment from "moment";
import { isEmpty } from "@/fp";
import { PREVIEW_FEATURES } from "./_preview.features";
import { UserStatus } from "@/views/admins/data/models";

export const getAnonymousUserId = gql2<void, string>(
  "anonymous-user-id",
  () => {
    return {
      query: `
    {
      entity(id:"anonymous-user"){
        id
      }
    }
    `,
      mapper(res) {
        const id = res.entity.id;
        return id;
      },
    };
  },
);
// /extproxy/integrations/teach-custom-label
const get_edition_status = apiCall<
  void,
  { active: boolean; endsAt: string; exhausted: boolean; edition: EditionStr }
>("get-edition-status", async () => {
  const res = await axios.get("/extproxy/integrations/edition?full=true");
  const {
    id: edition,
    revertsAt: trial_ends_at,
    isTrial = false,
    isTrialExhausted = false,
  } = res.data.edition;
  //  const is_standard = id === "standard";
  // const edition = is_standard && isTrial ? "launch" : id;

  return {
    success: true,
    value: {
      active: isTrial && moment(trial_ends_at).isAfter(new Date()),
      exhausted: isTrialExhausted,
      endsAt: trial_ends_at,
      edition,
    },
  };
});

// get , put
export const update_preferences = apiCall<{ [key: string]: any }, boolean>(
  "preferences.update",
  async (prefs) => {
    await axios.put("/user/preferences", prefs);
    return {
      success: true,
      value: true,
    };
  },
);

/* 
{
    "show": true,
    "org": {
        "name": "forged-org-19",
        "services": [
            "Auditlog",
            "Google",
            "Microsoft",
        ],
        "dataSourceApplications": [
            "onedrive",
            "sharepoint",
            "exchange_365",
            "ms_teams"
        ]
    },
    "edition": "enterprise"
}

*/

export const getUserStatus = apiCall<
  void,
  {
    user: LoggedUser;
    isActive: boolean;
    isOrgSuspended: boolean;
    dataAvailableFrom: string;
    features: { [key: string]: true };
    preferences: { [key: string]: any };
    in_motion: {
      is_google_org_only: boolean;
      is_ms_org_only: boolean;
      is_ms_and_google_org: boolean;
    };
  }
>("user-status", async () => {
  const res = await axios.get("/status/info");
  const {
    show,
    org: {
      status,
      orgContactEmail = "",
      services,
      dataSourceApplications = [
        "exchange_365",
        "ms_teams",
        "onedrive",
        "sharepoint",
        "gmail",
      ],
      // cognni by default
      partnerId = "cognni",
      orgName,
      name: alt_org_name,
      // Regular SingleUser
      type: org_type,
      features: org_features = [] as AppFeature[],
    },
    user: { email, name, restrictions = [], preferences = {} },
  } = res.data;

  let { dataAvailableFrom = "2022-10-17", edition } = res.data;

  const is_ms_org = (services as string[]).includes("Microsoft");
  const is_google_org = (services as string[]).includes("Google");
  const hasAuditLog: boolean = (services as string[]).includes("Auditlog");
  const showUI = show || (is_ms_org && !hasAuditLog);

  let trial = {
    active: false,
    endsAt: "",
    exhausted: false,
    edition: "launch" as EditionStr,
  };

  const features = org_features.reduce(
    (acc: Record<string, AppFeature>, item: AppFeature) => {
      acc[item.id] = item;
      return acc;
    },
    {} as Record<string, AppFeature>,
  );

  if (location.host === "preview.cognni.ai" || SharedState.is_local_dev) {
    dataAvailableFrom = "2010-01-01";
    edition = "standard";
    PREVIEW_FEATURES.forEach((feature) => {
      if (features[feature.id]) {
        // Feature exists in backoffice — keep the server's enabled value,
        // only supplement any missing metadata from the static defaults.
        features[feature.id] = {
          ...feature,
          enabled: features[feature.id].enabled,
        };
      } else if (feature.controllable) {
        // Controllable feature NOT in server response — the backoffice
        // removed it, meaning it was turned off. Default to disabled.
        features[feature.id] = { ...feature, enabled: false };
      } else {
        // Non-controllable feature not on server — use static preview default.
        features[feature.id] = feature;
      }
    });
    // Re-apply any local toggle overrides (from localStorage) on top.
    try {
      const overrides = JSON.parse(
        localStorage.getItem("sldx_feature_overrides") || "{}",
      );
      Object.entries(overrides).forEach(([id, enabled]) => {
        if (features[id]) {
          features[id] = { ...features[id], enabled: enabled as boolean };
        }
      });
    } catch (_) {
      /* ignore storage errors */
    }
  }

  if (edition) {
    trial.active = false;
    trial.endsAt = "";
    trial.exhausted = false;
    trial.edition = edition;
  } else {
    const edition_status_res = await get_edition_status();
    if (edition_status_res.success) {
      trial = edition_status_res.value;
    }
  }

  const value = {
    isActive: status === "Active",
    isOrgSuspended: status === "Suspended",
    dataAvailableFrom,
    features,
    preferences,
    in_motion: {
      is_google_org_only: is_google_org && !is_ms_org,
      is_ms_org_only: is_ms_org && !is_google_org,
      is_ms_and_google_org: is_ms_org && is_google_org,
    },
    user: {
      trial,
      auditLogConsent: hasAuditLog,
      services,
      dataSourceApplications,
      showUI,
      email,
      partnerId,
      restrictions,
      type:
        isEmpty(restrictions) || restrictions.length === 0
          ? "system"
          : "business",
      paymentEdition: toEdition(trial.edition),
      orgContactEmail,
      name,
      orgName: orgName ? orgName : alt_org_name,
      authorization: "",
    },
  };
  return {
    success: true,
    value,
  };
});

export const startTrial = apiCall<void, boolean>("trial-start", async () => {
  await axios.put("/extproxy/integrations/edition", {
    isTrial: true,
  });
  return {
    success: true,
    value: true,
  };
});

//#endregion
export const getReportAssetsCount = gql2(
  "assets",
  (types: InformationType[], timeFrames) => {
    const fileCount: CountParametersTier1Tier2 = {
      coordinate: {
        x: cord(AxisType.None, []),
        y: cord(AxisType.ContentLabel, []),
        z: timeFrames.total,
      },
      action: ActionEnum.List,
    };

    const sources: CountParametersTier1Tier2 = {
      coordinate: {
        x: cord(AxisType.Application, []),
        y: cord(AxisType.None, []),
        z: timeFrames.total,
      },
      pageSize: 1000,
      action: ActionEnum.List,
    };

    const entitiesCount = (type: AxisType): CountParametersTier1Tier2 => {
      return {
        coordinate: {
          x: cord(type, []),
          y: cord(AxisType.ContentLabel, []),
          z: timeFrames.total,
        },
        action: ActionEnum.UniqCount,
      };
    };

    const query = `
    {
      files: tier1count(params:${formatInput(fileCount)}){
         files
         y{value}
      }

      sources: tier1count(params:${formatInput(sources)}){
        count
         x{ value }
         y{ type }
      }

      folder: tier1count(params:${formatInput(entitiesCount(AxisType.Folder))}){
        count
        x{value}
        y{value}
      }

      employee: tier1count(params:${formatInput(
        entitiesCount(AxisType.Employee),
      )}){
        count
        y{value}
      }

      contact: tier1count(params:${formatInput(
        entitiesCount(AxisType.ExternalReceiver),
      )}){
        count
        y{value}
      }

     organization: tier1count(params:${formatInput(
       entitiesCount(AxisType.OrganizationReceiver),
     )}){
      count
      y{value}
    }

    }
    `;
    return {
      query,
      mapper: (res) => {
        const { folder, employee, contact, organization, sources } = res;

        const typesIds = types.map((t) => t.id);

        const countReduceReducer = (acc: number, item: { count: number }) =>
          (acc += item.count);

        const files = res.files.filter((f: any) =>
          typesIds.includes(f.y.value),
        );

        const filterL0ContentLabel = (item: any) => {
          if (item && item.y) {
            return (
              SharedState.l0InfoTypes.value.findIndex(
                (f: any) => f.id === item.y.value,
              ) > -1
            );
          }
          return false;
        };

        const filesCount = files
          .filter(filterL0ContentLabel)
          .reduce((acc: number, item: { files: number }) => {
            acc += item.files;
            return acc;
          }, 0);

        //.filter(filterL0ContentLabel)
        return {
          entities: {
            EMPLOYEE: employee.reduce(countReduceReducer, 0),
            CONTACT: contact.reduce(countReduceReducer, 0),
            ORGANIZATION: organization.reduce(countReduceReducer, 0),
            FILE: filesCount,
            FOLDER: folder.reduce(countReduceReducer, 0),
          },
          //@ts-ignore
          information: files.reduce((acc, item) => {
            const { y, files } = item;
            acc[y.value] = files;
            return acc;
          }, {}),
          //@ts-ignore
          sources: sources
            .filter((t: any) => t.y.type.toLowerCase() === "none")
            // @ts-ignore
            .reduce((acc, item) => {
              const { x, count } = item;
              acc[x.value] = count;
              return acc;
            }, {}),
        };
      },
    };
  },
);

export const getReportTime = gql2<void, { from: string; to: string }>(
  "report-time",
  () => {
    return {
      query: `
      {
        dateInfo{
          startTime
          endTime
        }
      }
    `,
      mapper: (res) => {
        const { startTime, endTime } = res.dateInfo;
        return { from: startTime, to: endTime };
      },
    };
  },
);

export const getItemLabelsHistory = gql2<string, ObjectLabelHistoryItem[]>(
  "item.get",
  (id: string) => {
    return {
      query: `
       {
        item(id:"${id}"){
          labelHistory{
            time
            userId
            userEmail
            generation
            labelId
            labelName
            parentId
            status
          }
        }
       }
      `,
      mapper: (res) => res.item.labelHistory,
    };
  },
);

//  0 < score <= 100
// https://dev.azure.com/shieldox/X2O/_workitems/edit/11862
export const update_custom_score_balance = apiCall<
  { id: string; score: number },
  boolean
>("accounts", async ({ id, score }) => {
  await axios.put("/score/custom", {
    type: "custom",
    id,
    score,
  });
  return {
    success: true,
    value: true,
  };
});

export const get_indexed_admins = apiCall<
  void,
  Record<string, { email: string; status: UserStatus }>
>("admins.get", async () => {
  const res = await axios.get("/admins");
  return {
    success: true,
    value: res.data.reduce(
      (
        acc: Record<string, { email: string; status: UserStatus }>,
        admin: any,
      ) => {
        acc[admin.id] = {
          email: admin.email,
          status: admin.status,
        };
        return acc;
      },
      {} as Record<string, { email: string; status: UserStatus }>,
    ),
  };
});
